import wootConstants from 'dashboard/constants/globals';
import { hasActiveAiAgent } from 'dashboard/helper/aiAgentMode';

// SiteUp: o celular da conversa usa o mesmo corte do projeto (Tailwind md /
// SMALL_SCREEN_BREAKPOINT = 768). Largura 0 = ainda nao mediu, trata como desktop
// para nao ligar o layout mobile no primeiro tick dos testes/jsdom.

export const MOBILE_HEADER_CONTROL_LIMIT = 5;

export function isSmallScreenWidth(width) {
  return (
    Number(width) > 0 && Number(width) < wootConstants.SMALL_SCREEN_BREAKPOINT
  );
}

// SiteUp: no celular a dica de Shift+Enter nao cabe e nao existe teclado.
// Desktop permanece com o texto longo de atalho.
export function composerPlaceholderI18nKey({
  isSmallScreen = false,
  isPrivate = false,
  isEditorDisabled = false,
  restrictionKind = null,
} = {}) {
  if (isEditorDisabled) {
    if (restrictionKind === 'whatsapp') {
      return 'CONVERSATION.FOOTER.MESSAGING_RESTRICTED_WHATSAPP';
    }
    if (restrictionKind === 'api') {
      return 'CONVERSATION.FOOTER.MESSAGING_RESTRICTED_API';
    }
    return 'CONVERSATION.FOOTER.MESSAGING_RESTRICTED';
  }
  if (isSmallScreen) {
    return isPrivate
      ? 'CONVERSATION.FOOTER.PRIVATE_MSG_INPUT_MOBILE'
      : 'CONVERSATION.FOOTER.MSG_INPUT_MOBILE';
  }
  return isPrivate
    ? 'CONVERSATION.FOOTER.PRIVATE_MSG_INPUT'
    : 'CONVERSATION.FOOTER.MSG_INPUT';
}

// SiteUp: o placeholder do ProseMirror e position:absolute, entao um texto
// de duas linhas estourava o campo (medido: 43px de altura, 56px de
// scrollHeight). No celular o ::before entra no fluxo e a altura acompanha.
export const MOBILE_COMPOSER_GROW_CLASS =
  '[&_.empty-node]:before:!relative [&_.ProseMirror-woot-style]:h-auto [&_.ProseMirror-woot-style]:overflow-y-auto';

export function composerGrowClass(isSmallScreen) {
  return isSmallScreen ? MOBILE_COMPOSER_GROW_CLASS : '';
}

// SiteUp: no celular, conversa curta comeca no topo. first:mt-auto no
// desktop continua empurrando as mensagens para a base (nao muda 1px).
export function threadAnchorClass(isSmallScreen) {
  return isSmallScreen ? '' : 'first:mt-auto';
}

// No celular, abrir a conversa cai na conversa. O painel de contato so aparece
// se o atendente pedir pelo menu (userOpenedContact).
export function shouldShowContactPanelOnOpen({
  isSmallScreen,
  userOpenedContact = false,
} = {}) {
  if (isSmallScreen) return Boolean(userOpenedContact);
  return true;
}

export function hasMobileAiContext(chat) {
  return hasActiveAiAgent(chat);
}

export function hasMobileFunnelContext(funnelItem) {
  return Boolean(funnelItem?.id || funnelItem?.funnel_stage);
}

export function shouldShowMobileContextStrip({ chat, funnelItem } = {}) {
  return hasMobileAiContext(chat) || hasMobileFunnelContext(funnelItem);
}

export function mobileAiStripState(chat) {
  if (!hasActiveAiAgent(chat)) return null;
  const paused = chat?.additional_attributes?.ai_agent?.paused === true;
  return paused ? 'paused' : 'active';
}

export function funnelStageFromItem(item, funnels = []) {
  if (!item) return null;
  const funnel =
    item.funnel ||
    funnels.find(entry => String(entry.id) === String(item.funnel_id));
  const stages = Object.entries(funnel?.stages || {})
    .map(([key, stage]) => ({
      key,
      name: stage?.name || key,
      position: Number(stage?.position ?? 0),
    }))
    .sort((a, b) => a.position - b.position);
  const index = Math.max(
    0,
    stages.findIndex(stage => stage.key === item.funnel_stage)
  );
  const name = stages[index]?.name || item.funnel_stage || '';
  if (!name) return null;
  return {
    name,
    index: index + 1,
    total: stages.length || 1,
  };
}

export const REQUIRED_MOBILE_MENU_ACTIONS = [
  'toggle_status',
  'mark_pending',
  'copy_id',
  'change_assignee',
  'toggle_ai',
  'schedule',
  'tasks',
  'labels',
  'participants',
  'open_kanban_card',
  'view_contact',
];

export function buildMobileConversationMenuItems({
  t = key => key,
  hasActiveAi = false,
  aiPaused = false,
  hasFunnelCard = false,
  isResolved = false,
  isSnoozed = false,
  isPending = false,
  muted = false,
  canCall = false,
  hasSla = false,
  hasMessagingWindow = false,
  isListHidden = false,
  statusActionsFirst = true,
  includeComposerActions = true,
  canGenerateLeadSheet = false,
} = {}) {
  const toggleStatusItem = {
    action: 'toggle_status',
    icon: isResolved || isSnoozed ? 'i-ph-arrow-u-up-left' : 'i-ph-check',
    label:
      isResolved || isSnoozed
        ? t('CONVERSATION.HEADER.REOPEN_ACTION')
        : t('CONVERSATION.HEADER.RESOLVE_ACTION'),
  };
  const statusItems = [
    toggleStatusItem,
    ...(!isPending
      ? [
          {
            action: 'mark_pending',
            icon: 'i-ph-circle',
            label: t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING'),
          },
        ]
      : []),
  ];
  // Módulo Imobiliária: a ficha do lead mora no menu do Resolver, então no
  // celular fica logo depois dos estados, como no menu da setinha do desktop.
  const leadSheetItems = canGenerateLeadSheet
    ? [
        {
          action: 'lead_sheet',
          icon: 'i-ph-clipboard-text',
          label: t('CONVERSATION.LEAD_SHEET.MENU_ITEM'),
          dividerBefore: statusActionsFirst,
        },
      ]
    : [];
  const items = [
    ...(statusActionsFirst ? [...statusItems, ...leadSheetItems] : []),
    {
      action: 'copy_id',
      icon: 'i-ph-copy',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_COPY_ID'),
      dividerBefore: statusActionsFirst,
    },
    {
      action: 'change_assignee',
      icon: 'i-ph-user-switch',
      label: t('CONVERSATION.HEADER.CHANGE_ASSIGNEE'),
    },
    {
      action: 'toggle_ai',
      icon: aiPaused ? 'i-ph-play' : 'i-ph-pause',
      label: aiPaused
        ? t('CONVERSATION.HEADER.RESUME_AI')
        : t('CONVERSATION.HEADER.PAUSE_AI_FULL'),
      disabled: !hasActiveAi,
    },
    {
      action: 'ai_configure',
      icon: 'i-ph-gear',
      label: t('CONVERSATION.HEADER.AI_CONFIGURE'),
      disabled: !hasActiveAi,
    },
    ...(!statusActionsFirst ? [toggleStatusItem, ...leadSheetItems] : []),
    {
      action: 'schedule',
      icon: 'i-ph-chat-circle-dots',
      label: t('CONVERSATION.HEADER.SCHEDULE_MESSAGE_QUICK'),
    },
    {
      action: 'tasks',
      icon: 'i-ph-check-square',
      label: t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.TITLE'),
    },
    {
      action: 'labels',
      icon: 'i-ph-tag',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_LABELS'),
    },
    {
      action: 'participants',
      icon: 'i-ph-users',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_PARTICIPANTS'),
    },
    {
      action: 'open_kanban_card',
      icon: 'i-ph-kanban',
      label: t('CONVERSATION.HEADER.CONTEXT_CHIPS.KANBAN.OPEN_CARD'),
      disabled: !hasFunnelCard,
    },
    {
      action: 'view_contact',
      icon: 'i-ph-user',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_CONTACT'),
    },
    {
      action: 'agenda',
      icon: 'i-ph-calendar',
      label: t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.TITLE'),
    },
    {
      action: 'timer',
      icon: 'i-ph-clock',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_TIMER'),
    },
    {
      action: 'pin_to_dock',
      icon: 'i-ph-push-pin',
      label: t('CONVERSATION.CARD_CONTEXT_MENU.PIN_TO_DOCK'),
    },
    {
      action: 'mark_unread',
      icon: 'i-ph-envelope',
      label: t('CONVERSATION.CARD_CONTEXT_MENU.MARK_AS_UNREAD'),
    },
    {
      action: muted ? 'unmute' : 'mute',
      icon: muted ? 'i-ph-speaker-low' : 'i-ph-speaker-slash',
      label: muted
        ? t('CONTACT_PANEL.UNMUTE_CONTACT')
        : t('CONTACT_PANEL.MUTE_CONTACT'),
    },
    {
      action: 'send_transcript',
      icon: 'i-ph-share',
      label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    },
    {
      action: 'note',
      icon: 'i-ph-note',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_NOTE'),
    },
    {
      action: 'emoji',
      icon: 'i-ph-smiley',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_EMOJI'),
    },
    {
      action: 'audio',
      icon: 'i-ph-microphone',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_AUDIO'),
    },
    {
      action: 'signature',
      icon: 'i-ph-pen-nib',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_SIGNATURE'),
    },
    {
      action: 'quoted_reply',
      icon: 'i-ph-quotes',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_QUOTED_REPLY'),
    },
    {
      action: 'waha_event',
      icon: 'i-ph-calendar',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_WAHA_EVENT'),
    },
    {
      action: 'location',
      icon: 'i-ph-map-pin',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_LOCATION'),
    },
    {
      action: 'whatsapp_templates',
      icon: 'i-ph-whatsapp-logo',
      label: t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES'),
    },
    {
      action: 'content_templates',
      icon: 'i-ph-file-text',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_CONTENT_TEMPLATES'),
    },
    {
      action: 'video_call',
      icon: 'i-ph-video-camera',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_VIDEO_CALL'),
    },
    {
      action: 'insert_article',
      icon: 'i-ph-article',
      label: t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH'),
    },
    {
      action: 'composer_actions',
      icon: 'i-ph-squares-four',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_COMPOSER_ACTIONS'),
    },
  ];

  if (canCall) {
    items.push({
      action: 'call',
      icon: 'i-ph-phone',
      label: t('CONVERSATION.HEADER.VOICE_CALL'),
    });
  }
  if (hasSla) {
    items.push({
      action: 'sla',
      icon: 'i-ph-timer',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_SLA'),
    });
  }
  if (hasMessagingWindow) {
    items.push({
      action: 'window',
      icon: 'i-ph-browser',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_WINDOW'),
    });
  }
  if (isListHidden) {
    items.push({
      action: 'finish_focus',
      icon: 'i-ph-check',
      label: t('CHAT_LIST.FOCUS_QUEUE.FINISH'),
    });
  }

  if (includeComposerActions) return items;
  const composerActions = new Set([
    'note',
    'emoji',
    'audio',
    'signature',
    'quoted_reply',
    'waha_event',
    'location',
    'whatsapp_templates',
    'content_templates',
    'video_call',
    'insert_article',
    'composer_actions',
  ]);
  return items.filter(item => !composerActions.has(item.action));
}
